import notification from "antd/lib/notification";
import { useTranslation } from "react-i18next";
import { BehaviorSubject, Observable, Subject } from "rxjs";

notification.config({
  placement: "bottomRight",
});

export enum messageType {
  SUCCESS,
  WARNING,
  ERROR,
}

export interface IMessage {
  title?: string;
  description?: string;
  type?: messageType;
}

const useCrudMessageHook = () => {
  const [translate] = useTranslation();
  const notifyUpdateItemSuccess = (description?: string) => {
    return notification.success({
      message: translate("general.update.success"),
      description,
    });
  };

  const notifyUpdateItemError = (description?: string) => {
    return notification.error({
      message: translate("general.update.error"),
      description,
    });
  };

  const notifyBadRequest = (description?: string) => {
    return notification.error({
      message: translate("general.badRequest"),
      description,
    });
  };

  const notifyUnAuthorize = (description?: string) => {
    return notification.error({
      message: translate("general.unAuthorize"),
      description,
    });
  };

  const notifyServerError = (description?: string) => {
    return notification.error({
      message: translate("general.serverError"),
      description,
    });
  };

  const notifyBEError = (description?: string) => {
    return notification.error({
      message: translate("general.beError"),
      description,
    });
  };

  const notifyIdleError = (description?: string) => {
    return notification.error({
      message: translate("general.idleError"),
      description,
    });
  };

  return {
    notifyUpdateItemSuccess,
    notifyUpdateItemError,
    notifyBadRequest,
    notifyUnAuthorize,
    notifyServerError,
    notifyBEError,
    notifyIdleError,
  };
};

export class AppMessageService {
  success$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false); // success subject for app message
  error$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false); // error subject for app message
  message$: Subject<IMessage>; // message subject for app message

  constructor() {
    this.message$ = new Subject<IMessage>();
  }

  _success: () => Observable<boolean> = () =>
    this.success$ as Observable<boolean>; // expose get success$ Observable
  _error: () => Observable<boolean> = () => this.error$ as Observable<boolean>; // expose get error$ as Observable
  setSuccess: () => void = () => {
    this.success$.next(true);
  };
  setError: () => void = () => {
    this.error$.next(true);
  };

  setMessage: (message: IMessage) => void = (message: IMessage) => {
    this.message$.next(message);
  };

  messageFactory(content: IMessage) {
    const { type, description, title } = content;
    if (type === messageType.SUCCESS) {
      return notification.success({
        message: title,
        description,
      });
    }
    if (type === messageType.WARNING) {
      return notification.warn({
        message: title,
        description,
      });
    }
    if (type === messageType.ERROR) {
      return notification.error({
        message: title,
        description,
      });
    }
  } // factory a snack from  messType, message as title and descripton as body

  handleNotify(message: IMessage) {
    return (value: boolean) => {
      if (value) {
        this.messageFactory(message);
      }
    };
  } // handle any Success message

  public useCRUDMessage = useCrudMessageHook;
}

const appMessageService = new AppMessageService();

export default appMessageService;
